import React, { useState, useEffect, useRef } from 'react';
import { useAuthUser } from 'react-auth-kit';

import { getThumbnailUrl } from 'src/utils/previews';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { getAssetMediaUrl } from 'src/api/functions/assetsApi';
import { retryOnTransientStatus } from 'src/api/utils/helpers';

import * as ISOLocales from 'iso-locales';

import VideoJsPlayer, { VideoTrack } from 'src/components/VideoJsPlayer/VideoJsPlayer';

import { addCacheBuster } from 'src/utils/previews';
import { Button, Loader } from 'src/components';

import styles from './Modal.module.scss';

type PreviewParams = {
  assetType: string;
  assetId: string;
  conversions: any;
  dimensions: [width?: number, height?: number];
  /** `thumbnailVersion(asset)`: a new value means a new thumbnail URL. */
  thumbnailVersion?: number;
  /**
   * Optional: report the resolved thumbnail URL upwards.
   *
   * Existed solely so the modal could hand the URL to the RBG viewer. That
   * viewer is gone (the cut-out panel fetches its own source), so nothing
   * consumes this today — kept optional rather than deleted because it is a
   * reasonable seam for a future sibling panel.
   */
  getURL?: (url: string) => void;
};

type TrackProps = {
  src: string;
  kind: string;
  label: string;
  default?: boolean;
  srcLang: string;
};

const videoKeys = [
  '240p-video',
  '240p-wm-video',
  '320p-video',
  '320p-wm-video',
  '360p-video',
  '360p-wm-video',
  '480p-video',
  '480p-wm-video',
  '720p-video',
  '720p-wm-video',
  '1080p-video',
  '1080p-wm-video',
];

/**
 * get_link answers with the URL as text, and a failure comes back as an error
 * OBJECT rather than a throw. Passing that object on as a URL crashed the
 * video player (`src.split is not a function`), so only a non-empty string
 * counts as a URL.
 */
const asMediaUrl = (result: unknown): string | null =>
  typeof result === 'string' && result.trim() !== '' ? result : null;

const ModalPreview: React.FC<PreviewParams> = ({
  assetType,
  assetId,
  conversions,
  dimensions,
  thumbnailVersion,
  getURL,
}) => {
  const [datatype] = assetType.split('/') as ['image' | 'video' | 'audio', string];

  const accessToken = useAccessToken();
  const auth = useAuthUser();

  const [mediaSrc, setMediaSrc] = useState<string>('');
  const [tracks, setTracks] = useState<TrackProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Images: the thumbnail URL that has finished loading. Loading is derived
  // from it rather than from `isLoading`, because the effect below re-runs on
  // a refetch that leaves the URL unchanged (e.g. saving an AI edit as a
  // conversion) — the <img> then never fires onLoad again, and a reset flag
  // left the panel spinning forever.
  const [loadedImageSrc, setLoadedImageSrc] = useState<string | null>(null);
  // The media URL could not be resolved: say so and offer a retry, instead of
  // "Loading media..." forever.
  const [mediaError, setMediaError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Each load gets a number; only the latest may write state, so a slow
  // response for the previous asset can't replace the current one's media.
  const loadRef = useRef(0);

  useEffect(() => {
    const load = ++loadRef.current;
    setIsLoading(true);
    setMediaError(false);
    setMediaSrc('');
    setTracks([]);
    getURL?.(mediaThumbnail);
    loadMediaData(() => load === loadRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, assetType, conversions, accessToken, reloadKey]);

  const mediaThumbnail = React.useMemo(() => {
    const baseUrl = getThumbnailUrl({
      width: dimensions[0] ? Math.min(dimensions[0], 800) : 800,
      height: dimensions[1] ? Math.min(dimensions[1], 600) : 600,
      id: assetId,
      jwt: accessToken,
      version: thumbnailVersion,
    });
    return addCacheBuster(baseUrl);
    // Rebuilt when the thumbnail changes (e.g. a storyboard frame was set),
    // not only when another asset opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, thumbnailVersion]);

  const possibleVideoKeys =
    datatype === 'video' ? videoKeys.filter((value) => Object.keys(conversions).includes(value)) : [];

  /** A signed URL for `key`, or null when it can't be had. Never throws. */
  const fetchMediaUrl = async (key: string, delivery: 'display' | 'download'): Promise<string | null> => {
    // Null until the auth state has rehydrated; `auth()!` used to throw here.
    const accessId = auth()?.accessId;
    if (!accessId) return null;
    try {
      // A 429/5xx is retried: get_link shares the API rate limit with the
      // burst of requests a cold asset page fires.
      const result = await retryOnTransientStatus(() =>
        getAssetMediaUrl(assetId, key, accessId, delivery, accessToken),
      );
      return asMediaUrl(result);
    } catch {
      return null;
    }
  };

  const setDefaultTrack = (srcLang: string) => {
    const { language } = navigator;
    const languages = [language, language.split('-')[0]].map((lang) => lang.toLowerCase());
    return languages.includes(srcLang.toLowerCase());
  };

  const processSubtitle = async ({ subtitle }: any): Promise<TrackProps | null> => {
    const [, langCode] = subtitle.split('_');
    const locale = ISOLocales.findByTag(langCode);

    const src = await fetchMediaUrl(subtitle, 'download');
    if (!src) return null;

    return {
      kind: 'subtitles',
      src,
      srcLang: langCode,
      label: `${locale?.language_local ?? locale?.language} ${locale?.region ? `(${locale?.region})` : ''}`,
      default: setDefaultTrack(langCode),
    };
  };

  const getMediaTracks = async (): Promise<TrackProps[]> => {
    const subtitles = Object.entries(conversions)
      .filter(([key]) => key.includes('subtitles'))
      .map(([subtitle, value]: [string, any]) => ({ ...value, subtitle }));

    const trackSources = await Promise.all(subtitles.map((subtitle) => processSubtitle(subtitle)));
    // A subtitle whose link failed is left out; the video still plays.
    return trackSources.filter((track): track is TrackProps => track !== null);
  };

  const loadMediaData = async (isCurrent: () => boolean) => {
    if (datatype === 'image') {
      // base image renders from the ODI thumbnail; <img onLoad> ends loading
      setMediaSrc('');
      return;
    }

    const [src, trackSources] = await Promise.all([
      fetchMediaUrl(possibleVideoKeys?.[0] ?? 'original', 'display'),
      // Subtitles are optional: whatever goes wrong there must not stop the
      // video from loading.
      getMediaTracks().catch(() => [] as TrackProps[]),
    ]);
    if (!isCurrent()) return;
    if (src) setMediaSrc(src);
    else setMediaError(true);
    setTracks(trackSources);
    setIsLoading(false);
  };

  if (datatype === 'video' || datatype === 'audio') {
    if (mediaError) {
      return (
        <div className={styles.loaderContainer} data-test="media-load-error">
          <h3>This {datatype} couldn't be loaded.</h3>
          <Button size="small" mode="secondary" onClick={() => setReloadKey((k) => k + 1)} datatest="media-load-retry">
            Try again
          </Button>
        </div>
      );
    }
    if (isLoading || !mediaSrc) {
      return (
        <div className={styles.loaderContainer}>
          <Loader />
          <h3>Loading media...</h3>
        </div>
      );
    }
    const playerTracks: VideoTrack[] = tracks.map((track) => ({
      ...track,
      default: track.default || tracks.length === 1,
    }));
    return (
      <section style={{ width: '100%' }}>
        <VideoJsPlayer key={mediaSrc} src={mediaSrc} poster={mediaThumbnail} tracks={playerTracks} />
      </section>
    );
  }

  const imageLoading = loadedImageSrc !== mediaThumbnail;
  // A failed load also ends the spinner: a broken image beats spinning forever.
  const markImageSettled = () => setLoadedImageSrc(mediaThumbnail);
  return (
    <>
      {imageLoading && (
        <div className={styles.loaderContainer}>
          <Loader />
          <h3>Loading media...</h3>
        </div>
      )}
      <img
        className={styles.previewImage}
        alt="Preview"
        data-test="preview-image"
        src={mediaThumbnail}
        onLoad={markImageSettled}
        onError={markImageSettled}
        hidden={imageLoading}
      />
    </>
  );
};

export { ModalPreview };
